import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  BatchUploadDocument,
  BatchUploadEntry,
  FinalizeBatchResponse,
  InitializeBatchUploadRequest,
  InitializeBatchUploadResponse,
} from '../models/batch-upload';
import { UploadConfigService } from '../../shared/services/upload-config.service';
import { PackLimitationsService } from '../../shared/services/pack-limitations.service';

/**
 * UI-facing status of a bulk upload run.
 * - initializing: calling /batch/init
 * - uploading:    PUTting files directly to S3
 * - finalizing:   calling /batch/:id/finalize
 */
export type BulkUploadStatus =
  | 'idle'
  | 'initializing'
  | 'uploading'
  | 'finalizing'
  | 'done'
  | 'error';

/** Reactive state the bulk upload component subscribes to. */
export interface BulkUploadState {
  status: BulkUploadStatus;
  total: number;
  uploaded: number;
  /** Files that never reached S3 after all retries. */
  failed: number;
}

/** Outcome of adding files to the queue, so the UI can surface toasts. */
export interface AddFilesResult {
  duplicates: string[];
  tooBig: string[];
}

/** Pairs a file with the presigned URL it must be PUT to. */
interface UploadTask {
  upload: BatchUploadEntry;
  file: File;
}

const INITIAL_STATE: BulkUploadState = {
  status: 'idle',
  total: 0,
  uploaded: 0,
  failed: 0,
};

/** Average processing time per document, in minutes (business estimate). */
const MINUTES_PER_FILE = 3.5;

/**
 * Max PUTs in flight at once. Firing all N uploads simultaneously exhausts the
 * browser's connection pool (Chrome throws net::ERR_INSUFFICIENT_RESOURCES past
 * a few hundred), so we drain the queue in bounded waves. Kept low because a
 * high request rate to a single S3 prefix triggers 503 SlowDown throttling.
 */
const UPLOAD_CONCURRENCY = 4;

/** Retries per file on transient (network / 5xx, esp. 503 SlowDown) failures. */
const MAX_UPLOAD_ATTEMPTS = 6;

/** Base delay for the exponential backoff, in ms (first retry window). */
const BACKOFF_BASE_MS = 500;

/** Upper bound on a single backoff window, in ms. */
const BACKOFF_MAX_MS = 10_000;

/** Pause before the final sweep that retries leftovers, in ms. */
const SWEEP_DELAY_MS = 2_000;

/**
 * Owns the bulk document upload: the file queue, its validation/limit rules,
 * and the end-to-end upload flow:
 *   1. init     -> reserve jobs, get presigned S3 URLs
 *   2. upload   -> PUT each file directly to S3
 *   3. finalize -> confirm uploads, enqueue AI analysis
 *
 * The component only feeds files in and renders the exposed signals.
 */
@Injectable({ providedIn: 'root' })
export class BatchUploadService {
  private http = inject(HttpClient);
  private uploadConfig = inject(UploadConfigService);
  private packLimitations = inject(PackLimitationsService);

  readonly files = signal<File[]>([]);

  // File Limit
  // Max files per batch as defined by the pack (0 = feature disabled)
  readonly defaultMaxFiles = computed(() =>
    this.packLimitations.bulkUploadFileLimit(),
  );
  // Effective cap: pack limit (from the API), bounded by remaining trips.
  // The absolute ceiling (BATCH_MAX_FILES) lives backend-side; pack.bulkUpload is always below it.
  readonly maxFiles = computed(() =>
    Math.min(this.packLimitations.remainingTrips(), this.defaultMaxFiles()),
  );
  readonly packLimitationWarning = computed(
    () => this.packLimitations.remainingTrips() < this.defaultMaxFiles(),
  );
  readonly isOverLimit = computed(() => this.files().length > this.maxFiles());
  readonly excessCount = computed(() =>
    Math.max(0, this.files().length - this.maxFiles()),
  );
  readonly filesToUpload = computed(() =>
    this.files().slice(0, this.maxFiles()),
  );
  readonly totalSizeMb = computed(() => {
    const bytes = this.files().reduce((sum, file) => sum + file.size, 0);
    return (bytes / 1024 / 1024).toFixed(2);
  });

  // Upload run state
  readonly state = signal<BulkUploadState>({ ...INITIAL_STATE });

  /** Determinate percentage during the S3 upload phase (shared by both views). */
  readonly uploadPercent = computed(() => {
    const { uploaded, total } = this.state();
    return total > 0 ? Math.round((uploaded / total) * 100) : 0;
  });

  isFileIncluded(index: number): boolean {
    return index < this.maxFiles();
  }

  estimatedMinutes(fileCount: number): number {
    return Math.round(fileCount * MINUTES_PER_FILE);
  }

  // ── Queue mutations ─────────────────────────────────────────────
  /**
   * Appends files to the queue, skipping ones too big or already queued.
   * Returns a summary so the component can show the relevant toasts.
   */
  addFiles(newFiles: File[]): AddFilesResult {
    const config = this.uploadConfig.AI_DOCUMENT;
    const current = this.files();
    const duplicates: string[] = [];
    const tooBig: string[] = [];
    const toAdd: File[] = [];

    for (const file of newFiles) {
      if (file.size > config.maxSize) {
        tooBig.push(file.name);
      } else if (current.some((existing) => this.isSameFile(existing, file))) {
        duplicates.push(file.name);
      } else {
        toAdd.push(file);
      }
    }

    if (toAdd.length > 0) {
      this.files.set([...current, ...toAdd]);
    }
    return { duplicates, tooBig };
  }

  removeFile(index: number): void {
    this.files.set(this.files().filter((_, i) => i !== index));
  }

  clearFiles(): void {
    this.files.set([]);
  }

  /**
   * Moves the file at `from` to position `to`.
   * Pass `files().length` as `to` to drop the file at the end.
   */
  reorderFile(from: number, to: number): void {
    const list = [...this.files()];
    if (from < 0 || from >= list.length || from === to) return;

    const [moved] = list.splice(from, 1);
    // Removing `from` shifts everything after it left by one.
    const target = from < to ? to - 1 : to;
    list.splice(target, 0, moved);
    this.files.set(list);
  }

  private isSameFile(a: File, b: File): boolean {
    return (
      a.name === b.name &&
      a.size === b.size &&
      a.lastModified === b.lastModified
    );
  }

  // ── Upload flow ─────────────────────────────────────────────────
  /**
   * Runs the full flow and drives `state`:
   *   init -> direct-to-S3 uploads -> finalize.
   * Returns the names of files that never reached S3 (empty on full success).
   * Throws only if the whole batch failed; the caller resets state.
   */
  async startBulkUpload(): Promise<string[]> {
    const files = this.filesToUpload();
    if (files.length === 0) return [];

    this.state.set({
      status: 'initializing',
      total: files.length,
      uploaded: 0,
      failed: 0,
    });

    // Step 1 — reserve jobs server-side, get presigned PUT URLs.
    const { batchId, uploads } = await firstValueFrom(
      this.getSignedUrls(files),
    );
    this.state.update((s) => ({ ...s, status: 'uploading' }));

    // Step 2 — upload every file straight to S3 in bounded waves.
    const pending: UploadTask[] = uploads.map((upload, i) => ({
      upload,
      file: files[i],
    }));
    let failed = await this.runUploads(pending);

    // Step 2b — one extra sweep. A 503 SlowDown is transient and does NOT
    // invalidate the presigned URL, so retrying the leftovers after a short
    // pause (letting S3 scale the prefix) recovers most of them.
    if (failed.length > 0) {
      await this.delay(SWEEP_DELAY_MS);
      failed = await this.runUploads(failed);
    }
    this.state.update((s) => ({ ...s, failed: failed.length }));

    // Step 3 — finalize. The backend HEAD-checks every job and decides per
    // file (uploaded -> enqueued, missing -> failed), so we just send batchId.
    this.state.update((s) => ({ ...s, status: 'finalizing' }));
    const result = await firstValueFrom(this.finalizeBatch(batchId));

    if (result.jobsQueued === 0) {
      throw new Error('No file could be processed.');
    }

    this.state.update((s) => ({ ...s, status: 'done' }));
    return failed.map((task) => task.file.name);
  }

  /**
   * Fire-and-forget orchestration for a background upload: runs the full flow
   * and drives `state` to a terminal status (`done` or `error`). Never rejects.
   *
   * The terminal state is intentionally NOT reset here: it stays so the open
   * modal can show its "done"/"error" view. The reset happens when the user
   * closes the modal (board.onBulkModalClose) or, in the minimized case, from
   * the floating widget's terminal effect (which also surfaces the toast).
   */
  async runUpload(): Promise<void> {
    if (this.filesToUpload().length === 0) return;

    try {
      await this.startBulkUpload();
    } catch {
      this.state.update((s) => ({ ...s, status: 'error' }));
    }
  }

  /** POST /ai/extract/batch/init — reserves jobs + returns presigned PUT URLs. */
  private getSignedUrls(files: File[]) {
    const documents: BatchUploadDocument[] = files.map((file) => ({
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || undefined,
    }));

    const body: InitializeBatchUploadRequest = { documents };

    return this.http.post<InitializeBatchUploadResponse>(
      `${environment.apiUrl}/ai/extract/batch/init`,
      body,
    );
  }

  /** POST /ai/extract/batch/:batchId/finalize — the backend resolves every job. */
  private finalizeBatch(batchId: string) {
    return this.http.post<FinalizeBatchResponse>(
      `${environment.apiUrl}/ai/extract/batch/${batchId}/finalize`,
      {},
    );
  }

  /**
   * Drains the queue with at most UPLOAD_CONCURRENCY PUTs in flight. Each worker
   * pulls the next task until the queue is empty and collects the ones that
   * failed every retry, so the caller can sweep them again with fresh waves.
   */
  private async runUploads(tasks: UploadTask[]): Promise<UploadTask[]> {
    const failed: UploadTask[] = [];
    let next = 0;
    const worker = async (): Promise<void> => {
      while (next < tasks.length) {
        const task = tasks[next++];
        try {
          await this.putFileToS3(task.upload.uploadUrl, task.file);
          this.state.update((s) => ({ ...s, uploaded: s.uploaded + 1 }));
        } catch {
          failed.push(task);
        }
      }
    };

    const workerCount = Math.min(UPLOAD_CONCURRENCY, tasks.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));
    return failed;
  }

  /**
   * PUTs one file to its presigned URL, retrying transient failures.
   * Uses fetch() (not HttpClient) to bypass the auth interceptor — an
   * Authorization header alongside a presigned URL makes S3 reject the PUT.
   *
   * Retries network errors and 5xx (notably 503 SlowDown) with exponential
   * backoff + full jitter, honoring a Retry-After header when present. A real
   * 4xx (bad/expired signature) is treated as terminal — no point retrying.
   */
  private async putFileToS3(url: string, file: File): Promise<void> {
    const headers: Record<string, string> = {};
    // Content-Type must match what the backend signed (file.type, or nothing).
    if (file.type) headers['Content-Type'] = file.type;

    for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
      const isLastAttempt = attempt === MAX_UPLOAD_ATTEMPTS;
      let response: Response;
      try {
        console.log('uploading file');
        response = await fetch(url, { method: 'PUT', body: file, headers });
      } catch {
        // Network-level failure (offline, ERR_INSUFFICIENT_RESOURCES): retryable.
        if (isLastAttempt) throw new Error('S3 upload failed: network error.');
        await this.delay(this.backoffDelay(attempt));
        continue;
      }

      if (response.ok) return;
      // 4xx (expired/invalid presigned URL) won't recover; only retry 5xx.
      if (response.status < 500 || isLastAttempt) {
        throw new Error(`S3 upload failed (${response.status}).`);
      }
      await this.delay(this.backoffDelay(attempt, response));
    }
  }

  /**
   * Exponential backoff with full jitter, capped at BACKOFF_MAX_MS. A 503
   * Retry-After header, when present, takes precedence. Full jitter (a random
   * delay within the window) desynchronizes the upload workers so their retries
   * don't re-create the request spike S3 is throttling.
   */
  private backoffDelay(attempt: number, response?: Response): number {
    const retryAfter = response ? this.parseRetryAfter(response) : null;
    if (retryAfter !== null) return retryAfter;

    const window = Math.min(
      BACKOFF_BASE_MS * 2 ** (attempt - 1),
      BACKOFF_MAX_MS,
    );
    return Math.random() * window;
  }

  /** Parses a Retry-After header (delta-seconds or HTTP-date) into ms, or null. */
  private parseRetryAfter(response: Response): number | null {
    const header = response.headers.get('Retry-After');
    if (!header) return null;

    const seconds = Number(header);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

    const date = Date.parse(header);
    return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Clears the queue and resets the upload state back to idle. */
  reset(): void {
    this.files.set([]);
    this.state.set({ ...INITIAL_STATE });
  }
}
